// Presentational primitives for the public talent-submission flow.
//
// Scoped deliberately to this flow. `components/Card` is used by 20 other views
// (admin, client, talent dashboard), so restyling it would redesign half the app
// as a side effect. Nothing here is imported outside `views/talent/submission`.
//
// Visual direction matches the public marketing site (`public-site/styles.css`,
// Direction 2 "Studio") — white ground, hairline rules, uppercase micro-labels,
// #237049 accent — because the submission page is linked straight off it and
// should not read as a different product.

import { Box, Text, Button } from '@chakra-ui/react'
import BaseSelect from 'components/Select'

const tokens = {
  green: '#237049',
  greenDark: '#103726',
  greenTint: '#f1f7f3',
  ink: '#111815',
  text: '#3b453f',
  muted: '#6f7a73',
  faint: '#a4ada7',
  line: '#e6eae7',
  line2: '#eff2f0',
  danger: '#b4232a',
}

// Small uppercase label. Used for panel eyebrows, section rules, step names.
const Eyebrow = ({ children, color = tokens.green, ...props }) => (
  <Text
    fontSize="11px"
    fontWeight="700"
    letterSpacing="0.2em"
    textTransform="uppercase"
    color={color}
    lineHeight="1.4"
    {...props}
  >
    {children}
  </Text>
)

// Replaces the solid-green title bar of `components/Card` with a white header,
// a title and a hairline. Same slot shape (title + children) so call sites
// stay a one-for-one swap.
const Panel = ({ eyebrow, title, children, containerProps = {}, bodyProps = {} }) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor={tokens.line}
    borderRadius="16px"
    overflow="hidden"
    boxShadow="0 1px 2px rgba(17,24,21,.04)"
    {...containerProps}
  >
    {(eyebrow || title) && (
      <Box
        px={{ base: 5, md: 8 }}
        pt={{ base: 6, md: 7 }}
        pb={{ base: 4, md: 5 }}
        borderBottomWidth="1px"
        borderColor={tokens.line2}
      >
        {eyebrow && <Eyebrow mb="10px">{eyebrow}</Eyebrow>}
        {title && (
          <Text
            fontSize={{ base: '26px', md: '30px' }}
            lineHeight="1.15"
            color={tokens.ink}
          >
            {title}
          </Text>
        )}
      </Box>
    )}
    <Box px={{ base: 5, md: 8 }} py={{ base: 6, md: 8 }} {...bodyProps}>
      {children}
    </Box>
  </Box>
)

// Full-width divider that names the group of fields beneath it. Sits inside the
// form grid, so it spans every column regardless of breakpoint.
const SectionRule = ({ label, hint, ...props }) => (
  <Box
    gridColumn="1 / -1"
    mt={{ base: 6, md: 8 }}
    mb={1}
    pt={4}
    borderTopWidth="1px"
    borderColor={tokens.line2}
    {...props}
  >
    <Eyebrow color={tokens.muted}>{label}</Eyebrow>
    {hint && (
      <Text mt="6px" fontSize="14px" color={tokens.muted} lineHeight="1.55">
        {hint}
      </Text>
    )}
  </Box>
)

// Three-dot progress rail for age group -> details -> done.
const Stepper = ({ steps, current }) => (
  <Box display="flex" alignItems="center" justifyContent="center" gap={{ base: 2, md: 3 }} flexWrap="wrap">
    {steps.map((label, i) => {
      const done = i < current
      const active = i === current
      return (
        <Box key={label} display="flex" alignItems="center" gap={{ base: 2, md: 3 }}>
          {i > 0 && <Box w={{ base: '18px', md: '34px' }} h="1px" bg={done || active ? tokens.green : tokens.line} />}
          <Box display="flex" alignItems="center" gap="8px">
            <Box
              w="7px"
              h="7px"
              borderRadius="50%"
              bg={done || active ? tokens.green : tokens.line}
              flex="none"
            />
            <Eyebrow color={active ? tokens.green : done ? tokens.text : tokens.faint}>
              {label}
            </Eyebrow>
          </Box>
        </Box>
      )
    })}
  </Box>
)

// Pill buttons matching `.btn` / `.btn.primary` on the marketing site.
const pillBase = {
  fontSize: '12px',
  fontWeight: '700',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  borderRadius: '999px',
  height: 'auto',
  px: '26px',
  py: '15px',
  borderWidth: '1.5px',
  transition: 'background .16s, color .16s, border-color .16s',
}

const PrimaryButton = ({ children, ...props }) => (
  <Button
    {...pillBase}
    bg={tokens.green}
    bgColor={tokens.green}
    borderColor={tokens.green}
    color="white"
    _hover={{ bg: tokens.greenDark, bgColor: tokens.greenDark, borderColor: tokens.greenDark }}
    _active={{ bg: tokens.greenDark, bgColor: tokens.greenDark }}
    _disabled={{ opacity: 0.45, cursor: 'not-allowed' }}
    {...props}
  >
    {children}
  </Button>
)

const SecondaryButton = ({ children, ...props }) => (
  <Button
    {...pillBase}
    bg="white"
    bgColor="white"
    borderColor={tokens.ink}
    color={tokens.ink}
    _hover={{ bg: tokens.ink, bgColor: tokens.ink, color: 'white' }}
    _disabled={{ opacity: 0.45, cursor: 'not-allowed' }}
    {...props}
  >
    {children}
  </Button>
)

// One `sx` block that restyles every native input, textarea, label and
// chakra-react-select inside the form. Applied to a wrapper so the ~40 existing
// FormControls keep their react-hook-form wiring untouched — no per-field edits.
const formFieldStyles = {
  '.chakra-form__label': {
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: tokens.muted,
    mb: '7px',
  },
  'input, textarea': {
    fontSize: '15px',
    color: tokens.ink,
    bg: 'white',
    borderWidth: '1px',
    borderColor: tokens.line,
    borderRadius: '10px',
    _hover: { borderColor: tokens.faint },
    _focus: { borderColor: tokens.green, boxShadow: `0 0 0 3px ${tokens.greenTint}` },
    _focusVisible: { borderColor: tokens.green, boxShadow: `0 0 0 3px ${tokens.greenTint}` },
    _placeholder: { color: tokens.faint },
    _readOnly: { bg: tokens.line2, color: tokens.muted },
  },
  'input:not([type="checkbox"])': { height: '46px' },
  textarea: { py: '12px', lineHeight: '1.55' },
  '.chakra-form__error-message': {
    fontSize: '13px',
    color: tokens.danger,
    mt: '6px',
  },
  '.chakra-form__helper-text': {
    fontSize: '13px',
    color: tokens.muted,
    lineHeight: '1.5',
    mt: '7px',
  },
}

// chakra-react-select renders its control through emotion with generated class
// names only, so there is no stable selector for CSS to hook. Styling has to go
// through its own `chakraStyles` API — hence this pre-styled wrapper. Passing
// props after the defaults keeps every existing call site's props authoritative.
const selectChakraStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '46px',
    borderRadius: '10px',
    background: 'white',
    borderColor: state.isFocused ? tokens.green : tokens.line,
    boxShadow: state.isFocused ? `0 0 0 3px ${tokens.greenTint}` : 'none',
    _hover: { borderColor: state.isFocused ? tokens.green : tokens.faint },
  }),
  valueContainer: (base) => ({ ...base, px: 4, fontSize: '15px' }),
  placeholder: (base) => ({ ...base, color: tokens.faint, fontSize: '15px' }),
  singleValue: (base) => ({ ...base, color: tokens.ink }),
  multiValue: (base) => ({
    ...base,
    background: tokens.greenTint,
    color: tokens.greenDark,
    borderRadius: '6px',
  }),
  // The stock indicator is a filled grey block with square corners, which is
  // what broke the rounded field edge.
  indicatorSeparator: (base) => ({ ...base, display: 'none' }),
  dropdownIndicator: (base) => ({
    ...base,
    background: 'transparent',
    borderWidth: 0,
    px: 3,
    color: tokens.muted,
  }),
  clearIndicator: (base) => ({ ...base, color: tokens.muted }),
  menu: (base) => ({
    ...base,
    borderRadius: '12px',
    overflow: 'hidden',
    borderWidth: '1px',
    borderColor: tokens.line,
    boxShadow: '0 10px 30px rgba(17,24,21,.12)',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '15px',
    background: state.isSelected
      ? tokens.green
      : state.isFocused
        ? tokens.greenTint
        : 'white',
    color: state.isSelected ? 'white' : tokens.ink,
  }),
}

const Select = (props) => <BaseSelect chakraStyles={selectChakraStyles} {...props} />

export {
  tokens,
  Select,
  Eyebrow,
  Panel,
  SectionRule,
  Stepper,
  PrimaryButton,
  SecondaryButton,
  formFieldStyles,
}
